import axios from "axios";
import { showPrintFailure, reportPrintError, POPUP_KINDS } from "./printFailureNotice";

// TSK-5056: a print whose till template isn't configured (or can't be read) used to skip silently,
// print a blank slip, or throw inside the printer. Every such case now tells the cashier which print
// was skipped. BUG-2570: the text itself now comes from printFailureNotice (one catalogue for every
// print failure); the template name stays in the console line for support.
export const PRINT_TEMPLATES = {
  sales: "Sales Print Template",
  kot: "KOT Print Template",
  estimate: "Estimate Print Template",
  cancelKot: "Cancel KOT Print Template",
  tillClose: "Till Close Template",
  salesReport: "Sales Report Template",
  giftCard: "Gift Card / Credit Note / Voucher Print Template",
};

// printName values the printers pass -> print kind in the catalogue. Unknown names (e.g. a
// receiptType) keep their own name as the label.
const PRINT_NAME_KINDS = {
  Receipt: "bill",
  Bill: "preparedBill",
  KOT: "kot",
  "Cancel KOT": "cancelKot",
  Estimate: "estimate",
  "Till Close report": "tillClose",
  "Sales Report": "salesReport",
  "Gift Card": "giftCard",
  "Credit Note": "giftCard",
};
const toFailure = (templateName, printName) => {
  const knownKind = PRINT_NAME_KINDS[printName] || (templateName === PRINT_TEMPLATES.giftCard ? "giftCard" : null);
  return knownKind ? { kind: knownKind } : { kind: "print", label: printName };
};

// Each returns a promise: for Close Till it resolves when the cashier presses OK on the popup.
export const notifyTemplateNotConfigured = (templateName, printName, ref) => {
  console.error(`[PrintTemplate] ${printName} not printed: ${templateName} is not configured for this till.`);
  return showPrintFailure({ ...toFailure(templateName, printName), ref, cause: "formatMissing" });
};

export const notifyTemplateInvalid = (templateName, printName, ref) => {
  console.error(`[PrintTemplate] ${printName} not printed: ${templateName} is not in a valid format.`);
  return showPrintFailure({ ...toFailure(templateName, printName), ref, cause: "formatInvalid" });
};

export const notifyNoPrinterSelected = (kind = "print", ref, displayRef) => showPrintFailure({ kind, ref, displayRef, cause: "noPrinterSelected" });

// Every printer file POSTs to `${hardwareController.imageUrl}printer` and most only console.error a
// failure. One response interceptor turns any failed call to the print service into a visible error.
// BUG-2570: printers tag their request with printTag(kind, ref) so the message names what did not
// print; Close Till / day end (POPUP_KINDS) are skipped here because their printer awaits the
// answer and shows the popup itself.
// CW controller: <pole_display> / {"cash_drawer":...}. Openbravo controller: <display> / <opendrawer/>.
export const isNonPrintBody = (body) => /pole_display|cash_drawer|<display>|<opendrawer\s*\/?>/.test(body || "");

let printServiceNotifierInstalled = false;
export const installPrintServiceErrorNotifier = () => {
  if (printServiceNotifierInstalled) return;
  printServiceNotifierInstalled = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const config = error?.config || {};
      const url = config.url || "";
      // Pole display (fires on every cart add) and cash drawer share this endpoint but aren't prints.
      // Their senders set notAPrint; the body check covers any sender that does not (BUG-2570
      // staging: the Openbravo pole display's <output><display> raised "Print not printed" on every add).
      const body = typeof config.data === "string" ? config.data : "";
      const isNonPrintCommand = config.notAPrint === true || isNonPrintBody(body);
      const isPrintServiceCall = config.method?.toLowerCase() === "post" && /printer\/?$/.test(url) && !isNonPrintCommand;
      if (isPrintServiceCall && !axios.isCancel(error) && !POPUP_KINDS.has(config.printKind)) {
        reportPrintError({ kind: config.printKind || "print", ref: config.printRef, displayRef: config.printDisplayRef, error }).catch((e) =>
          console.error("[PrintFailure] could not show the print failure:", e?.message)
        );
      }
      return Promise.reject(error);
    }
  );
};

// Parses a JSON (CW hardware controller) template; returns null and shows the matching error when
// the template is missing or unparseable, so callers can simply bail out on null.
export const parseTemplateOrNotify = (templateCode, templateName, printName, ref) => {
  if (!templateCode) {
    notifyTemplateNotConfigured(templateName, printName, ref);
    return null;
  }
  try {
    const parsed = JSON.parse(templateCode);
    if (!parsed || !Array.isArray(parsed.elements)) throw new Error("template has no elements array");
    return parsed;
  } catch (error) {
    console.error(`[PrintTemplate] ${templateName} parse failed for ${printName}:`, error?.message);
    notifyTemplateInvalid(templateName, printName, ref);
    return null;
  }
};
